import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/store/auth";
import { useTheme } from "@/contexts/ThemeContext";
import { Particles } from "@/components/polish/Particles";
import {
  Languages, Sun, Moon, Loader2, Mail, Lock, Eye, EyeOff, Check,
  Zap, Activity, MapPin,
} from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// (Google button is scaffolded as disabled — full OAuth wiring happens when GOOGLE_CLIENT_ID is provided)
const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="h-4 w-4">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.3 29.3 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.3 6.5 28.9 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.4-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.3 6.5 28.9 4.5 24 4.5c-7.6 0-14.1 4.3-17.7 10.2z"/>
    <path fill="#4CAF50" d="M24 43.5c4.7 0 9-1.7 12.3-4.6l-5.7-4.8C28.9 35.9 26.5 36.7 24 36.7c-5.2 0-9.6-3.4-11.2-8l-6.6 5.1C9.7 39.2 16.3 43.5 24 43.5z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.4-2.5 4.5-4.7 5.9l5.7 4.8C39.2 36.3 43.5 30.9 43.5 24c0-1.2-.1-2.4 0.1-3.5z"/>
  </svg>
);

const SHOWCASE_KPIS = [
  { id: "mwh",    icon: Zap,      value: "12 MWh", label: "Deployed capacity" },
  { id: "sites",  icon: MapPin,   value: "48",     label: "Active sites" },
  { id: "uptime", icon: Activity, value: "99.2%",  label: "Fleet uptime" },
];

export default function Login() {
  const { t, i18n } = useTranslation();
  const { theme, toggle } = useTheme();
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [shake, setShake] = useState(false);
  const [success, setSuccess] = useState(false);

  const switchLang = () => i18n.changeLanguage(i18n.language?.startsWith("hi") ? "en" : "hi");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      setSuccess(true);
      // Phase 6C-d — CUSTOMER role lands on the self-service portal, not the
      // staff home. All other roles keep the existing "/" landing.
      const target = useAuth.getState().user?.role === "CUSTOMER" ? "/portal" : "/";
      setTimeout(() => navigate(target), 280);
    } else {
      setErr(res.error || t("login.errorInvalid"));
      setShake(true);
      setTimeout(() => setShake(false), 460);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: success ? 0 : 1 }}
        transition={{ duration: 0.3 }}
        className="min-h-screen w-full bg-[#040D1C] text-white relative overflow-hidden flex flex-col lg:flex-row"
        data-testid="login-page"
      >
        {/* ---------------- Top-right utilities (always on top) ---------------- */}
        <div className="fixed top-4 right-4 flex items-center gap-2 z-50">
          <button
            onClick={switchLang}
            data-testid="login-lang-toggle"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-xs uppercase tracking-widest backdrop-blur-md"
          >
            <Languages className="h-4 w-4" />
            {i18n.language?.startsWith("hi") ? "Hi" : "En"}
          </button>
          <button
            onClick={toggle}
            data-testid="login-theme-toggle"
            className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 backdrop-blur-md"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
        </div>

        {/* ---------------- Left brand showcase ---------------- */}
        <section
          data-testid="login-showcase"
          className="relative lg:w-[60%] lg:min-h-screen flex flex-col justify-between px-6 py-8 sm:px-10 sm:py-10 lg:px-14 lg:py-12 overflow-hidden border-b lg:border-b-0 lg:border-r border-white/5"
        >
          {/* Mesh gradient layers + grid + vignette + drifting particles */}
          <div className="login-mesh-a" />
          <div className="login-mesh-b" />
          <div className="login-grid" />
          <Particles count={14} />
          <div className="absolute inset-0 pointer-events-none"
               style={{ background: "radial-gradient(ellipse at top, transparent 30%, rgba(4,13,28,0.65) 100%)" }} />

          {/* Top: logo -- D14a fix: at <768px the wordmark's 14:1 aspect
              ratio blows past the viewport width. Swap to the square
              `mark` variant on narrow screens and shrink the height so
              the marketing header never clips.
              D52w micro-fix: on login the PRIMARY brand is the APP name
              ("Sharda Energy OS"). The horizontal wordmark PNG bakes the
              COMPANY name ("Sharda Energy Tech"), so we drop it in favor
              of the square mark + explicit app-name text, and demote
              the company name to a small attribution subtitle. */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.5 }}
            className="relative flex items-center gap-3 min-w-0 max-w-full"
            data-testid="login-brand"
          >
            <Logo size={32} kind="mark" />
            <div className="leading-tight min-w-0">
              <div className="font-heading font-black uppercase tracking-[0.14em] text-white text-lg sm:text-xl truncate sm:whitespace-normal"
                    data-testid="login-app-name">
                Sharda Energy OS
              </div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-white/45 truncate sm:whitespace-normal">
                A Sharda Energy Tech company
              </div>
            </div>
          </motion.div>

          {/* Center: headline */}
          <div className="relative mt-12 lg:mt-0">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.7 }}
              className="max-w-2xl"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-sky/40 bg-sky/10 text-sky text-[10px] uppercase tracking-[0.32em] mb-5">
                <span className="h-1.5 w-1.5 rounded-full bg-sky animate-pulse" />
                BESS Operations Platform
              </div>
              <h1 className="font-heading font-black uppercase leading-[0.95] tracking-[0.04em]
                             text-4xl sm:text-5xl lg:text-6xl xl:text-7xl">
                <span className="block text-white">Power.</span>
                <span className="block brand-gradient-text">Connectivity.</span>
                <span className="block text-white/95">Precision.</span>
              </h1>
              <p className="mt-5 text-sm sm:text-base text-white/65 max-w-xl font-body">
                Unified Business OS for Sharda Energy Tech — battery, sites, sales, service
                and finance, all in one futuristic command center.
              </p>
            </motion.div>
          </div>

          {/* Bottom: KPI showcase cards */}
          <div className="relative mt-10 lg:mt-0 grid grid-cols-3 gap-3 sm:gap-4 max-w-xl">
            {SHOWCASE_KPIS.map((k, i) => {
              const Icon = k.icon;
              return (
                <motion.div
                  key={k.id}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.55 + i * 0.08, duration: 0.5 }}
                  data-testid={`showcase-kpi-${k.id}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-3.5 sm:p-4"
                >
                  <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-lg bg-gradient-primary flex items-center justify-center mb-2 shadow-md shadow-sky/30">
                    <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                  </div>
                  <div className="font-body font-bold text-lg sm:text-xl tabular-nums tracking-tight animate-pulse-soft">
                    {k.value}
                  </div>
                  <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-white/55 mt-0.5">
                    {k.label}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* ---------------- Right form panel ---------------- */}
        <section className="relative lg:w-[40%] lg:min-h-screen flex items-center justify-center px-4 py-10 sm:px-8 lg:px-12">
          {/* faint backdrop tint */}
          <div className="absolute inset-0 pointer-events-none"
               style={{ background: "radial-gradient(ellipse at 80% 20%, rgba(30,150,252,0.10), transparent 60%)" }} />

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            data-testid="login-card"
            className={[
              "relative w-full max-w-md rounded-[20px] p-7 sm:p-9",
              "border border-white/10 backdrop-blur-2xl",
              "bg-white/[0.035]",
              "shadow-[0_20px_60px_rgba(0,0,0,0.45)]",
              shake ? "animate-shake" : "",
            ].join(" ")}
          >
            <div className="mb-6">
              <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-[0.12em]">
                WELCOME BACK
              </h2>
              <p className="text-sm text-white/60 mt-1.5 font-body">Sign in to continue</p>
            </div>

            <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form" noValidate>
              <div className="field">
                <Mail className="field-icon h-4 w-4" />
                <input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder=" "
                  autoComplete="email"
                  required
                />
                <label htmlFor="email" className="field-label">{t("login.email")}</label>
              </div>

              <div className="field">
                <Lock className="field-icon h-4 w-4" />
                <input
                  id="password"
                  data-testid="login-password-input"
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder=" "
                  autoComplete="current-password"
                  required
                />
                <label htmlFor="password" className="field-label">{t("login.password")}</label>
                <button
                  type="button"
                  className="field-trailing"
                  onClick={() => setShowPass((v) => !v)}
                  aria-label={showPass ? "Hide password" : "Show password"}
                  data-testid="login-password-toggle"
                >
                  {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              <div className="flex items-center justify-end -mt-1">
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-link"
                  className="text-xs text-sky hover:text-white transition-colors"
                >
                  {t("login.forgot")}
                </Link>
              </div>

              <AnimatePresence>
                {err && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    data-testid="login-error"
                    className="text-sm rounded-xl px-3 py-2.5 bg-rose-500/10 border border-rose-500/40 text-rose-200"
                  >
                    {err}
                  </motion.div>
                )}
              </AnimatePresence>

              <Button
                type="submit"
                disabled={submitting || success}
                data-testid="login-submit-button"
                className="w-full h-12 bg-gradient-primary text-white font-heading tracking-[0.24em] text-sm
                           hover:brightness-110 hover:shadow-[0_10px_30px_rgba(30,150,252,0.35)]
                           transition-all rounded-2xl"
              >
                {submitting
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : success
                    ? <><Check className="h-4 w-4 mr-2" /> SIGNED IN</>
                    : "SIGN IN"}
              </Button>

              {/* Divider */}
              <div className="relative my-1">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
                <div className="relative flex justify-center text-[10px] uppercase tracking-[0.28em] text-white/45">
                  <span className="px-3 bg-transparent">Or continue with</span>
                </div>
              </div>

              <Button
                type="button"
                disabled
                aria-disabled="true"
                data-testid="login-google-button"
                title={t("login.googleDisabled")}
                variant="outline"
                className="w-full h-11 gap-2 bg-white/[0.02] border-white/10 text-white/55 hover:bg-white/[0.04] cursor-not-allowed rounded-2xl"
              >
                <GoogleIcon />
                <span className="text-sm">{t("login.google")}</span>
                <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-white/40">Soon</span>
              </Button>
            </form>
          </motion.div>
        </section>
      </motion.div>
    </AnimatePresence>
  );
}
